import { Fragment, useState, useEffect, useRef, useCallback } from 'react';
import { createRefetchContainer, graphql } from 'react-relay/legacy';
import { useIntl, FormattedMessage } from 'dibs-react-intl';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { validate, validateField } from 'dibs-address-validator/exports/validate';
import { Fields } from '../../utils/constants';
import { getZipCodeBase } from '../../utils/helpers';

// components
import FirstName from '../fields/FirstName';
import LastName from '../fields/LastName';
import Company from '../fields/Company';
import Country from '../fields/Country';
import Line1 from '../fields/Line1';
import Line2 from '../fields/Line2';
import City from '../fields/City';
import State from '../fields/State';
import StateUS from '../fields/StateUS';
import ZipCode from '../fields/ZipCode';
import CityState from '../fields/CityState';
import PhoneNumber from '../fields/PhoneNumber';
import Submit from '../inputs/Submit';
import AddressLineAutocomplete from '../fields/AddressLineAutocomplete';
import { Tooltip } from 'dibs-elements/exports/Tooltip';

import styles from '../../styles/main.scss';

const hasValidErrors = errors => {
    return Object.keys(errors).some(errorName => !!errors[errorName]);
};

let ControlledAddressForm = props => {
    const {
        viewer,
        theme = {},
        address = {},
        autoFocus = false,
        fieldSize,
        fields: _fields = Fields,
        showAllErrors,
        isSaving,
        disableForm,
        children,
        renderChildren = () => null,
        hideSubmit,
        hidePhoneNumber,
        hidePhoneNumberTip,
        hideCompany,
        submitLabel,
        disableSubmit,
        showCityAndState: _showCityAndState,
        onSubmit: _onSubmit = () => null,
        validations,
        relay,
        onUpdate,
        onBlur: _onBlur = () => null,
        disabled: _disabled = [],
        isEditing = false,
        isMobile,
        hasRegistrationFormErrors,
        enableAddressAutocomplete,
    } = props;

    const fields = _fields.filter(
        f => !((f === 'company' && hideCompany) || (f === 'phoneNumber' && hidePhoneNumber))
    );
    const { geography } = viewer;
    const { country = 'US', zipCode = '', city, state } = address;

    const intl = useIntl();

    // state
    const [phoneTooltipVisible, setPhoneToolTipVisible] = useState(false);
    const [addressTooltipVisible, setAddressTooltipVisible] = useState(false);
    const [phoneTooltipSeen, setPhoneToolTipSeen] = useState(false);
    const [addressTooltipSeen, setAddressTooltipSeen] = useState(false);
    const prevCountryRef = useRef(country);
    const prevZipCodeLookUpRef = useRef(viewer.zipCodeLookUp);
    const prevZipCodeRef = useRef(zipCode);
    const [blurred, setBlurred] = useState(
        fields.reduce(
            (o, f) => ({
                ...o,
                // If zip code is already present it should be blurred for a field validation.
                [f]: isEditing || (f === 'zipCode' && zipCode && zipCode !== '0'),
            }),
            {}
        )
    );
    const [disableBrowserAutocomplete, setDisableBrowserAutocomplete] = useState({});
    const disabled = fields.reduce((o, f) => ({ ...o, [f]: _disabled.includes(f) }), {});
    const [hasFormValidationError, setHasFormValidationError] = useState(false);
    const [hasSubmittedForm, setHasSubmittedForm] = useState(false);
    const [showCityAndState, setShowCityAndState] = useState(
        _showCityAndState ||
            !!disabled.zipCode ||
            country !== 'US' ||
            ((zipCode || '').length >= 5 && !(city && state))
    );
    const autocompleteDetailsGettingFilled = useRef(false);

    const onValidate = (countryToValidate = '') => {
        // When country change we want to validate fields by that country.
        // This doesn't happen during on country change event.
        const addressToValidate = countryToValidate
            ? { ...address, country: countryToValidate }
            : address;
        return validate(addressToValidate, {
            fields,
            isPhoneRequired: !hidePhoneNumber,
            ...validations,
        });
    };

    const onValidateField = useCallback(
        ({ field, value }) =>
            validateField(value, {
                field,
                isCountryUS: country === 'US',
                isPhoneRequired: !hidePhoneNumber,
                ...validations,
            }),
        [country, hidePhoneNumber, validations]
    );

    const [{ errors, isValid }, updateFormStatus] = useState(onValidate());

    const zipCodeBase = getZipCodeBase(zipCode);
    const hasZipCodeBaseChanged = zipCodeBase !== getZipCodeBase(prevZipCodeRef.current);
    const hasCountryChanged = prevCountryRef.current !== country;
    prevZipCodeRef.current = zipCode;
    prevCountryRef.current = country;

    const nextZipCodeLookUp = viewer.zipCodeLookUp;

    useEffect(() => {
        //refetch zipCode
        const hasLookup = Array.isArray(nextZipCodeLookUp);
        const haveEditConditionsPassed = (hasZipCodeBaseChanged || hasCountryChanged) && isEditing;

        const shouldFetchGeography =
            (!isEditing || haveEditConditionsPassed) &&
            country === 'US' &&
            zipCode?.length >= 5 &&
            (hasZipCodeBaseChanged || hasCountryChanged || !hasLookup);
        if (shouldFetchGeography) {
            relay.refetch(
                {
                    country,
                    zipCode: zipCodeBase,
                    canCheckZipCode: true,
                },
                null,
                null,
                { force: true }
            );
        }
    }, [
        hasCountryChanged,
        zipCode,
        country,
        hasZipCodeBaseChanged,
        isEditing,
        nextZipCodeLookUp,
        relay,
        zipCodeBase,
    ]);

    useEffect(() => {
        // turn off this hook for when values are filled from autocomplete
        if (autocompleteDetailsGettingFilled.current) {
            return;
        }

        // If zipCodeLookUp or country has changed update city and state

        const updateValidate = (cityValue = '', stateValue = '', zipCodeValue) => {
            onUpdate({ field: 'city', value: cityValue });
            onUpdate({ field: 'state', value: stateValue });
            const errorMessages = {
                city: onValidateField({ field: 'city', value: cityValue }).error,
                state: onValidateField({ field: 'state', value: stateValue }).error,
            };
            if (zipCodeValue) {
                //Zip code value is already updated by onChange
                errorMessages.zipCode = onValidateField({
                    field: 'zipCode',
                    value: zipCodeValue,
                }).error;
            }
            const updatedErrors = { ...errors, ...errorMessages };
            updateFormStatus({ errors: updatedErrors, isValid: !hasValidErrors(updatedErrors) });
        };

        const noResultFound = Array.isArray(nextZipCodeLookUp) && !nextZipCodeLookUp.length;

        const lookedUpAdress = nextZipCodeLookUp?.[0] || {};
        const nextCity = lookedUpAdress.city || '';
        const nextState = lookedUpAdress.state || '';

        const prevLookedUpAdress = prevZipCodeLookUpRef.current?.[0] || {};
        const hasPrevCityChanged = nextCity !== prevLookedUpAdress.city;
        const hasPrevStateChanged = nextState !== prevLookedUpAdress.state;

        const isNextCityAndStatePresent = !!(nextCity && nextState);
        const isStateAndCityFetchableByZipCode = hasZipCodeBaseChanged && zipCode?.length >= 5;
        const shouldUpdate =
            isNextCityAndStatePresent &&
            (hasPrevCityChanged || hasPrevStateChanged || isStateAndCityFetchableByZipCode);

        if (shouldUpdate || hasCountryChanged) {
            if (hasCountryChanged) {
                //only empty fields should be blurred on country change
                setBlurred({ ...blurred, zipCode: !!zipCode?.length, state: false, city: false });
                setShowCityAndState(true);
                updateValidate('', '', zipCode);
            } else {
                setShowCityAndState(country !== 'US');
                if (country === 'US') {
                    updateValidate(nextCity, nextState);
                }
            }
        }

        const hasZipCodeLookUpChanged = prevZipCodeLookUpRef.current !== nextZipCodeLookUp;
        if (
            noResultFound &&
            !isNextCityAndStatePresent &&
            (hasZipCodeLookUpChanged || isStateAndCityFetchableByZipCode)
        ) {
            if (country === 'US') {
                setBlurred({
                    ...blurred,
                    state: true,
                    city: true,
                });
            }
            setShowCityAndState(!(city && state));
            updateValidate(city, state);
        }

        prevZipCodeLookUpRef.current = nextZipCodeLookUp;
    }, [
        hasCountryChanged,
        zipCode,
        hasZipCodeBaseChanged,
        city,
        state,
        country,
        blurred,
        errors,
        onUpdate,
        nextZipCodeLookUp,
        onValidateField,
    ]);

    //handlers
    const onZipCodeDropDown = ({ value }) => {
        setBlurred({ ...blurred, city: true, state: true });
        setShowCityAndState(value === 'otherCityState');
    };

    const onAddressAutocompleteDetailsReceived = details => {
        const detailsFields = ['country', 'zipCode', 'city', 'state'];

        // set form field values
        autocompleteDetailsGettingFilled.current = true;
        detailsFields.forEach(field => onUpdate({ field, value: details[field] }));
        autocompleteDetailsGettingFilled.current = false;

        // update field errors (most often this should clear any existing errors)
        const fieldErrors = {};
        detailsFields.forEach(field => {
            /*
             * If country is returned from autocomplete details - it's the newest country we should base our validations on.
             * We cannot use onValidateField here because its country value will be outdated since useCallback did not have a chance to recreate the function yet.
             */
            const currentCoutryValue = details.country ?? country;
            const fieldValue = details[field];

            const error = validateField(fieldValue, {
                field,
                isCountryUS: currentCoutryValue === 'US',
                isPhoneRequired: !hidePhoneNumber,
                ...validations,
            }).error;

            fieldErrors[field] = error;
        });

        setDisableBrowserAutocomplete({
            ...disableBrowserAutocomplete,
            ...detailsFields.reduce((acc, field) => {
                acc[field] = details[field] ? true : false;
                return acc;
            }, {}),
        });

        const updatedErrors = { ...errors, ...fieldErrors };
        updateFormStatus({ errors: updatedErrors, isValid: !hasValidErrors(updatedErrors) });
    };

    const onSubmit = () => {
        const { isValid: isValidForm, errors: errorMessages } = onValidate();
        const onSubmitErrors = {
            ...errorMessages,
            ...(errors.phoneNumber ? { phoneNumber: errors.phoneNumber } : {}),
        };
        const isValidSubmitForm = isValidForm && !errors.phoneNumber;
        const onSubmitAddress = {
            ...address,
            ...{
                phoneNumber: errors.phoneNumber ? '' : address.phoneNumber,
            },
        };
        _onSubmit({
            address: onSubmitAddress,
            isValid: isValidSubmitForm,
            errors: onSubmitErrors,
        });

        //Untouched fields need to be blurred to show error messages
        setBlurred(
            Object.keys({
                ...onSubmitErrors,
                ...onSubmitAddress,
            }).reduce((obj, field) => ({ ...obj, [field]: true }), {})
        );

        setHasFormValidationError(!isValidSubmitForm);
        setHasSubmittedForm(true);
    };

    const onChange = ({ name, value, isValid: isValidValue }) => {
        const isPhoneField = name === 'phoneNumber';
        onUpdate({ field: name, value });

        //should validate here after country change to avoid extra errors for US and other countries
        if (name === 'country' && address.country !== value) {
            const { isValid: isValidForm, errors: validationErrors } = onValidate(value);
            updateFormStatus({ errors: validationErrors, isValid: isValidForm });
        }

        if (blurred[name]) {
            const { error = null } = onValidateField({
                field: name,
                value: isPhoneField && !isValidValue ? '' : value,
            });
            const errorMessages = { ...errors, [name]: error };
            updateFormStatus({ errors: errorMessages, isValid: !hasValidErrors(errorMessages) });
        }

        if (name === 'line1') {
            setAddressTooltipSeen(true);
        }
        if (isPhoneField) {
            setPhoneToolTipSeen(true);
        }
    };

    const onBlur = ({ name, value = '', isValid: isValidValue }) => {
        const isPhoneField = name === 'phoneNumber';
        const { error = null } = onValidateField({
            field: name,
            value: isPhoneField && !isValidValue ? '' : value,
        });
        if (_onBlur) {
            const errorMessage = !value.trim() ? null : error?.defaultMessage || error;
            _onBlur({ field: name, value, error: errorMessage });
        }

        setBlurred({ ...blurred, [name]: true });

        const hasErrors = hasValidErrors(errors);
        const showFormError = hasErrors && hasSubmittedForm;
        setHasFormValidationError(!!showFormError);

        if (!hasErrors) {
            setHasSubmittedForm(false);
        }
        const errorMessages = { ...errors, [name]: error };
        updateFormStatus({ errors: errorMessages, isValid: !hasValidErrors(errorMessages) });

        if (name === 'line1') {
            setAddressTooltipVisible(false);
            setAddressTooltipSeen(false);
        }
        if (isPhoneField) {
            setPhoneToolTipVisible(false);
            setPhoneToolTipSeen(false);
        }
    };

    //render logic
    const formFields = fields.reduce(
        (obj, field) => ({
            ...obj,
            [field]: {
                fieldSize,
                value: address[field] || '',
                error: errors[field] ? intl.formatMessage(errors[field]) : null,
                isBlurred: showAllErrors || blurred[field],
                disabled: isSaving || disableForm || disabled[field],
                disableAutocomplete: disableBrowserAutocomplete[field],
                onChange: onChange,
                onBlur: onBlur,
            },
        }),
        {}
    );

    const isCountryUS = country === 'US';
    const isCityStateKnown = !!(city && state);
    const countries =
        geography &&
        geography.countries.map(_country => ({
            label: _country.name,
            value: _country.abbreviation,
        }));
    const usStates =
        geography &&
        geography.usStates.map(_state => ({
            label: _state.name,
            value: _state.abbreviation,
        }));
    const cityStateOptions = [
        { value: 'cityState', label: `${city}, ${state}` },
        { value: 'otherCityState', label: 'Other city and state' },
    ];

    const showName = !!(formFields.firstName && formFields.lastName);
    const showCompany = !!formFields.company;
    const showCountry = !!formFields.country;
    const showLine1 = !!formFields.line1;
    const showLine2 = !!formFields.line2;
    const showZipCode = !!formFields.zipCode;
    const showCity = !isCountryUS || showCityAndState || !!disabled.city;
    const showState = !isCountryUS;
    const showStateUS = isCountryUS && (showCityAndState || !!disabled.state);
    const showCityStateDropDown =
        isCountryUS && !showCityAndState && isCityStateKnown && !disabled.zipCode;
    const showCityStateLabel = isCountryUS && !showCityStateDropDown && !isCityStateKnown;
    const showPhoneNumber = !!formFields.phoneNumber;

    const labelClasses = classNames(styles.right, styles.textWrapper);
    const showMobileTooltip = setTooltipVisible => () => {
        if (isMobile) {
            setTooltipVisible(true);
        }
    };
    return (
        <div className={theme.wrapper} data-tn="dibs-address-form-wrapper">
            <form className={theme.form}>
                {showName &&
                    (isMobile ? (
                        <Fragment>
                            <FirstName
                                {...formFields.firstName}
                                autoFocus={autoFocus}
                                isMobile={isMobile}
                            />
                            <LastName {...formFields.lastName} isMobile={isMobile} />
                        </Fragment>
                    ) : (
                        <div className={styles.group} data-field="displayName">
                            <FirstName
                                {...formFields.firstName}
                                autoFocus={autoFocus}
                                isMobile={isMobile}
                            />
                            <LastName {...formFields.lastName} isMobile={isMobile} />
                        </div>
                    ))}

                {showLine1 && (
                    <Fragment>
                        {isMobile && (
                            <div
                                className={classNames(
                                    { [styles.hidden]: addressTooltipSeen },
                                    styles.tooltipContainer
                                )}
                            >
                                <Tooltip
                                    isVisible={addressTooltipVisible}
                                    align="center"
                                    direction="top"
                                    type="small"
                                    onClose={() => setAddressTooltipVisible(false)}
                                >
                                    <div className={styles.toolTipContainer}>
                                        <div>
                                            <FormattedMessage
                                                id="dc.ControlledAddressForm.addressTooltip1"
                                                defaultMessage="Enter address to see"
                                            />
                                        </div>
                                        <div>
                                            <FormattedMessage
                                                id="dc.ControlledAddressForm.addressTooltip2"
                                                defaultMessage="available shipping methods"
                                            />
                                        </div>
                                    </div>
                                </Tooltip>
                            </div>
                        )}
                        <div className={styles.addressLineContainer}>
                            {enableAddressAutocomplete ? (
                                <AddressLineAutocomplete
                                    {...formFields.line1}
                                    environment={relay.environment}
                                    onFocus={showMobileTooltip(setAddressTooltipVisible)}
                                    onDetailsReceived={onAddressAutocompleteDetailsReceived}
                                />
                            ) : (
                                <Line1
                                    {...formFields.line1}
                                    onFocus={showMobileTooltip(setAddressTooltipVisible)}
                                />
                            )}
                        </div>
                    </Fragment>
                )}
                {showCompany && <Company {...formFields.company} />}
                {showLine2 && <Line2 {...formFields.line2} />}
                {showCountry && <Country {...formFields.country} options={countries} />}

                {showCity && <City {...formFields.city} />}
                {showState && <State {...formFields.state} />}
                {showStateUS && <StateUS {...formFields.state} options={usStates} />}

                {showZipCode && (
                    <div className={styles.group} data-field="zipCode">
                        <ZipCode {...formFields.zipCode} isCountryUS={isCountryUS} />
                        {showCityStateDropDown && (
                            <CityState
                                value="cityState"
                                city={city}
                                state={state}
                                options={cityStateOptions}
                                isBlurred={blurred.zipCode}
                                disabled={isSaving || disableForm}
                                onChange={onZipCodeDropDown}
                            />
                        )}
                        {showCityStateLabel && (
                            <div className={labelClasses}>
                                <span className={styles.text}>
                                    <FormattedMessage
                                        id="dc.ControlledAddressForm.zipCode"
                                        defaultMessage="Enter ZIP code for City and State"
                                    />
                                </span>
                            </div>
                        )}
                    </div>
                )}

                {showPhoneNumber && (
                    <div className={styles.positioned}>
                        <div className={styles.group} data-field="phoneNumber">
                            {isMobile && !hidePhoneNumberTip && (
                                <div
                                    className={classNames(
                                        { [styles.hidden]: phoneTooltipSeen },
                                        styles.tooltipContainer
                                    )}
                                >
                                    <Tooltip
                                        isVisible={phoneTooltipVisible}
                                        align="center"
                                        direction="top"
                                        type="small"
                                        onClose={() => setPhoneToolTipVisible(false)}
                                    >
                                        <FormattedMessage
                                            id="dc.ControlledAddressForm.phoneTooltip"
                                            defaultMessage="For shipping questions only"
                                        />
                                    </Tooltip>
                                </div>
                            )}
                            <PhoneNumber
                                {...formFields.phoneNumber}
                                onFocus={showMobileTooltip(setPhoneToolTipVisible)}
                                isMobile={isMobile}
                                countryCode={formFields?.country?.value}
                                environment={relay.environment}
                            />
                            {!isMobile && !hidePhoneNumberTip && (
                                <div className={labelClasses}>
                                    <span className={styles.text}>
                                        <FormattedMessage
                                            id="dc.ControlledAddressForm.shippingQuestions"
                                            defaultMessage="For shipping questions only"
                                        />
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                )}
                {children}
                {renderChildren({ address, errors, isValid })}
            </form>
            {!hideSubmit && (
                <Submit
                    label={submitLabel}
                    disabled={disableSubmit || isSaving}
                    wrapperClass={theme.submit}
                    isSaving={isSaving}
                    onClick={onSubmit}
                />
            )}
            {(hasFormValidationError || hasRegistrationFormErrors) && (
                <div className={styles.formCTAError}>
                    <FormattedMessage
                        id="dc.ControlledAddressForm.formErrorMessage"
                        defaultMessage="Please correct errors to continue."
                    />
                </div>
            )}
        </div>
    );
};

ControlledAddressForm.propTypes = {
    viewer: PropTypes.object.isRequired,
    relay: PropTypes.object.isRequired,
    address: PropTypes.object,
    autoFocus: PropTypes.bool,
    theme: PropTypes.object,
    showAllErrors: PropTypes.bool,
    showCityAndState: PropTypes.bool,
    hidePhoneNumber: PropTypes.bool,
    hidePhoneNumberTip: PropTypes.bool,
    hideCompany: PropTypes.bool,
    hideSubmit: PropTypes.bool,
    disableSubmit: PropTypes.bool,
    isSaving: PropTypes.bool,
    fieldSize: PropTypes.oneOf(['small', 'medium', 'large']),
    fields: PropTypes.arrayOf(PropTypes.string),
    validations: PropTypes.object,
    disableForm: PropTypes.bool,
    disabled: PropTypes.array,
    renderChildren: PropTypes.func,
    submitLabel: PropTypes.node,
    onUpdate: PropTypes.func.isRequired,
    onBlur: PropTypes.func,
    onSubmit: PropTypes.func,
    children: PropTypes.any,
    isEditing: PropTypes.bool,
    isMobile: PropTypes.bool,
    hasRegistrationFormErrors: PropTypes.bool,
    enableAddressAutocomplete: PropTypes.bool,
};

// reassinging class to refetch container allows the graphql
// query to use AddressForm.getFragment for classic mode
ControlledAddressForm = createRefetchContainer(
    ControlledAddressForm,
    {
        viewer: graphql`
            fragment ControlledAddressForm_viewer on Viewer
            @argumentDefinitions(
                zipCode: { type: "String", defaultValue: "" }
                country: { type: "String", defaultValue: "" }
                canCheckZipCode: { type: "Boolean", defaultValue: false }
            ) {
                geography {
                    countries {
                        name
                        abbreviation
                    }
                    usStates {
                        name
                        abbreviation
                    }
                }
                zipCodeLookUp: zipCode(zipCode: $zipCode, country: $country)
                    @include(if: $canCheckZipCode) {
                    city
                    state
                }
            }
        `,
    },
    graphql`
        query ControlledAddressFormRefetchQuery(
            $zipCode: String = ""
            $country: String = ""
            $canCheckZipCode: Boolean!
        ) {
            viewer {
                ...ControlledAddressForm_viewer
                    @arguments(
                        zipCode: $zipCode
                        country: $country
                        canCheckZipCode: $canCheckZipCode
                    )
            }
        }
    `
);

export default ControlledAddressForm;
